import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import WayfinderLocationTemplate from "./WayfinderLocationTemplate";
import UseToast from "../UseToast";
import useS3Upload from "../../hooks/useS3Upload";
import LanguageSelector from "./LanguageSelector";
import LocationVideoProgress from "./LocationVideoProgress";
import useCurrentLanguage from "../../hooks/useCurrentLanguage";

const WayfinderLocation = () => {
  const navigate = useNavigate();
  const { userId, locationId } = useParams();
  const { showErrorToast, showSuccessToast } = UseToast();

  // Language management
  const { currentLanguage, updateLanguage } = useCurrentLanguage(userId);

  // Store original text directions for comparison
  const [originalTextDirections, setOriginalTextDirections] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [buttonColor, setButtonColor] = useState("#000000");

  // File states for uploads
  const [photoFile, setPhotoFile] = useState(null);
  const [avatarVideoFile, setAvatarVideoFile] = useState(null);
  const [mapVideoFile, setMapVideoFile] = useState(null);
  const [logoBeforeMapFile, setLogoBeforeMapFile] = useState(null);
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [clickSoundFile, setClickSoundFile] = useState(null);
  const [pngFile, setPngFile] = useState(null);
  const [btnIcon, setBtnIcon] = useState(null);

  // Corner logo files
  const [upperLeftLogoFile, setUpperLeftLogoFile] = useState(null);
  const [upperRightLogoFile, setUpperRightLogoFile] = useState(null);
  const [bottomLeftLogoFile, setBottomLeftLogoFile] = useState(null);
  const [bottomRightLogoFile, setBottomRightLogoFile] = useState(null);
  const [backButtonFile, setBackButtonFile] = useState(null);

  // Step-by-Step Directions State
  const [stepByStepConfig, setStepByStepConfig] = useState({
    useGlobal: true,
    useParent: false,
    selectedParentId: null,
    selectedParentType: null,
    codeInsert1: '',
    opening: '',
    mapTimeDelay: null,
    codeInsert2: '',
    stepByStepOpening: '',
    stepByStepDirections: '',
    codeInsert3: '',
    closing: '',
    codeInsert4: '',
    globalQrClosing: {
      enabled: true,
      text: '',
    },
    codeInsert5: '',
  });

  // Parent options for step-by-step directions
  const [parentOptions, setParentOptions] = useState([]);

  // Uploading states for MainWayfinderMedia
  const [uploadingStates, setUploadingStates] = useState({
    upperLeft: false,
    upperRight: false,
    bottomLeft: false,
    bottomRight: false,
    backButton: false,
    locationPhoto: false,
  });

  // S3 Upload hook
  const { uploadFile: uploadToS3, uploading: s3Uploading } = useS3Upload({
    signedUrlEndpoint: "/generate-signed-url",
    onSuccess: (fileUrl, file) => {
      showSuccessToast(`${file.name} uploaded successfully`);
    },
    onError: () => {
      showErrorToast("Upload failed");
    },
  });

  // Initial state matching the schema
  const [item, setItem] = useState({
    name: "",
    description: "",
    status: true,
    enabled: true,
    websiteUrl: "",
    airportUrl: "",
    textDirections: "",
    spaceName: "",
    externalId: "", // MappedIn enterprise-location external ID
    custom: false,

    // HeyGen API Key
    heygen_api_key_id: "", // MongoDB ID reference
    heygen_key_id: "", // Actual HeyGen API key value

    // Avatar API Key
    avatar_api_key_id: "", // MongoDB ID reference
    avatar_key_id: "", // Actual avatar key value

    // Voice API Key
    voice_api_key_id: "", // MongoDB ID reference
    voice_key_id: "", // Actual voice key value

    // Avatar Video
    avatarVideo: {
      fileUrl: "",
      fileName: "",
      enabled: false,
      captionFile: ''
    },

    // Enable custom avatar video
    enableCustomAvatarVideo: false,

    totalSteps: 0,

    // Map Video
    mapVideo: {
      fileUrl: "",
      fileName: "",
      enabled: false,
    },

    // Enable map video
    enableMapVideo: false,

    // Map delay time in seconds
    mapDelayTime: 2,

    // QR Code
    // qrCode: {
    //   fileUrl: "",
    //   fileName: "",
    //   enabled: false,
    //   width: 100,
    //   height: 100,
    // },

    logoBeforeMap: {
      fileUrl: '',
      fileName: '',
      enabled: '',
      width: '',
    },

    // Operation hours
    operationHours: [
      {
        day: "monday",
        open: "09:00",
        close: "17:00",
        isOpen: true,
        isFullDay: false,
      },
      {
        day: "tuesday",
        open: "09:00",
        close: "17:00",
        isOpen: true,
        isFullDay: false,
      },
      {
        day: "wednesday",
        open: "09:00",
        close: "17:00",
        isOpen: true,
        isFullDay: false,
      },
      {
        day: "thursday",
        open: "09:00",
        close: "17:00",
        isOpen: true,
        isFullDay: false,
      },
      {
        day: "friday",
        open: "09:00",
        close: "17:00",
        isOpen: true,
        isFullDay: false,
      },
      {
        day: "saturday",
        open: "10:00",
        close: "16:00",
        isOpen: true,
        isFullDay: false,
      },
      {
        day: "sunday",
        open: "10:00",
        close: "16:00",
        isOpen: true,
        isFullDay: false,
      },
    ],

    // Button settings
    // globalSettings: {
    //   cornerRadius: 0,
    //   height: 50,
    //   width: 150,
    //   outlineColor: '#000000',
    //   outlineThickness: 1,
    //   fillColor: '#ffffff',
    //   font: 'Arial',
    //   fontColor: '#127E10',
    //   fontSize: 16,
    //   textAlign: 'center',
    //   marginBottom: 2,
    //   lineHeight: 1,
    //   padding: 10,
    //   shadow: {
    //     angle: 5,
    //     size: 10,
    //     distance: 5,
    //     alpha: 0.5,
    //     color: '#FF5733',
    //   },
    //   highlight: {
    //     borderColor: '#000000',
    //     fillColor: '#ffffff',
    //     fontColor: '#000000',
    //   },
    //   clickSound: '',
    //   clickSoundFilename: null,
    //   clickSoundVolume: 1,
    //   pngBtnenabled: false,
    //   hideBtnText: false,
    //   btnPng: null,
    //   btnPngFileName: null,
    // },

    customSettings: {
      cornerRadius: 0,
      height: 50,
      width: 150,
      outlineColor: "#000000",
      outlineThickness: 1,
      fillColor: "#ffffff",
      font: "Arial",
      fontColor: "#127E10",
      fontSize: 16,
      textAlign: "center",
      marginBottom: 2,
      lineHeight: 1,
      padding: 10,
      shadow: {
        angle: 5,
        size: 10,
        distance: 5,
        alpha: 0.5,
        color: "#FF5733",
      },
      highlight: {
        borderColor: "#000000",
        fillColor: "#ffffff",
        fontColor: "#000000",
      },
      clickSound: "",
      clickSoundFilename: null,
      clickSoundVolume: 1,
      pngBtnenabled: false,
      hideBtnText: false,
      btnPng: null,
      btnPngFileName: null,
    },

    uploadedUrls: {
      upperLeft: {
        fileUrl: "",
        fileName: "",
        enabled: false,
        width: 100,
        height: 100,
      },
      upperRight: {
        fileUrl: "",
        fileName: "",
        enabled: false,
        width: 100,
        height: 100,
      },
      bottomLeft: {
        fileUrl: "",
        fileName: "",
        enabled: false,
        width: 100,
        height: 100,
      },
      bottomRight: {
        fileUrl: "",
        fileName: "",
        enabled: false,
        width: 100,
        height: 100,
      },
      backButton: {
        fileUrl: "",
        fileName: "",
        enabled: false,
        width: 100,
        height: 100,
      },
      locationPhoto: {
        fileUrl: "",
        fileName: "",
        enabled: false,
        width: 100,
        height: 100,
      },
    },

    // Parent references
    parentCategories: [],
    parentSubcategories: [],
    subLocations: [],

    mapPosition: {
      widthPx: 1000, // ~28% of 1920px
      heightPx: 2000, // ~75% of 1440px
      top: null, // 10%
      right: null, // 3%
      left: null,
      bottom: null,
    },

    textDirectionsMultiLang: {},

    distanceFromStart: null,
  });

  useEffect(() => {
    if (userId && locationId) {
      fetchLocationData();
      fetchSiteBrandColor();
      fetchStepByStepConfig();
      fetchParentOptions();
    }
  }, [userId, locationId]);

  const fetchSiteBrandColor = async () => {
    try {
      const res = await axios.get(`/auth/aiform/all/${userId}`);
      console.log("res", res);
      setButtonColor(res.data?.UserData?.brandColor || "#000000");
    } catch (err) {
      console.error("Failed to fetch brand color:", err);
    }
  };

  // Fetch Step-by-Step Directions Configuration
  const fetchStepByStepConfig = async () => {
    try {
      const response = await axios.get(`/api/wayfinder/step-by-step-directions/location/${userId}/${locationId}`);
      if (response.data.success) {
        setStepByStepConfig(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching step-by-step directions:", error);
    }
  };

  // Fetch parent options (categories and subcategories this location belongs to)
  const fetchParentOptions = async () => {
    try {
      // Fetch all categories
      const categoriesRes = await axios.get(`/api/wayfinder/get-categories/${userId}`);
      const categories = categoriesRes.data?.data || [];

      // Fetch all subcategories
      const subcategoriesRes = await axios.get(`/api/wayfinder/subcategory/${userId}`);
      const subcategories = subcategoriesRes.data?.data || [];

      // Filter to only include parents that contain this location
      const parentCats = categories.filter(cat => 
        cat.navigation?.locations?.some(loc => String(loc._id || loc) === locationId)
      ).map(cat => ({
        _id: cat._id,
        name: cat.name?.en || cat.buttonText?.en || 'Unnamed Category',
        type: 'Category',
        mapTimeDelay: null, // Will be fetched from step-by-step config
      }));

      const parentSubCats = subcategories.filter(sub => 
        sub.navigation?.locations?.some(loc => String(loc._id || loc) === locationId)
      ).map(sub => ({
        _id: sub._id,
        name: sub.name?.en || sub.buttonText?.en || 'Unnamed SubCategory',
        type: 'SubCategory',
        mapTimeDelay: null, // Will be fetched from step-by-step config
      }));

      // Fetch step-by-step configs for each parent to get mapTimeDelay
      const parentsWithDelay = await Promise.all([
        ...parentCats.map(async (parent) => {
          try {
            const configRes = await axios.get(`/api/wayfinder/step-by-step-directions/category/${userId}/${parent._id}`);
            return {
              ...parent,
              mapTimeDelay: configRes.data?.data?.mapTimeDelay,
            };
          } catch (err) {
            return parent;
          }
        }),
        ...parentSubCats.map(async (parent) => {
          try {
            const configRes = await axios.get(`/api/wayfinder/step-by-step-directions/subcategory/${userId}/${parent._id}`);
            return {
              ...parent,
              mapTimeDelay: configRes.data?.data?.mapTimeDelay,
            };
          } catch (err) {
            return parent;
          }
        }),
      ]);

      setParentOptions(parentsWithDelay);
    } catch (error) {
      console.error("Error fetching parent options:", error);
    }
  };

  // Save Step-by-Step Directions Configuration
  const saveStepByStepConfig = async () => {
    try {
      // Include currentLanguage in the payload
      const payload = {
        ...stepByStepConfig,
        currentLanguage: currentLanguage || 'en'
      };
      
      await axios.post(`/api/wayfinder/step-by-step-directions/location/${userId}/${locationId}`, payload);
      showSuccessToast("Step-by-step directions configuration saved successfully!");
      
      // Refresh location data to get updated textDirectionsMultiLang
      await fetchLocationData();
    } catch (error) {
      console.error("Error saving step-by-step directions:", error);
      showErrorToast("Failed to save step-by-step directions configuration");
    }
  };

  const fetchLocationData = async () => {
    if (!locationId || !userId) return;

    try {
      setLoading(true);
      const res = await axios.get(`/api/wayfinder/get-locations/${userId}`);

      if (!res.data?.data) {
        showErrorToast("No location data found");
        return;
      }

      const location = res.data.data.find((l) => l._id === locationId);

      console.log("Location data", location)

      if (!location) {
        showErrorToast("Location not found");
        navigate(`/wayfinder-main/${userId}`);
        return;
      }

      // Use location data directly with defaults as fallback
      const locationData = {
        name: location.name || "",
        buttonText: location.buttonText || "",
        description: location.description || "",
        status: location.status ?? true,
        enabled: location.enabled ?? true,
        websiteUrl: location.websiteUrl || "",
        airportUrl: location.airportUrl || "",
        textDirections: location.textDirections || "",
        spaceName: location.spaceName || "",
        externalId: location.externalId || "", // MappedIn enterprise-location external ID
        custom: location.custom ?? false,

        // HeyGen API Key
        heygen_api_key_id: location.heygen_api_key_id || "",
        heygen_key_id: location.heygen_key_id || "",

        // Avatar API Key
        avatar_api_key_id: location.avatar_api_key_id || "",
        avatar_key_id: location.avatar_key_id || "",

        // Voice API Key
        voice_api_key_id: location.voice_api_key_id || "",
        voice_key_id: location.voice_key_id || "",

        // Avatar Video
        avatarVideo: {
          fileUrl: location.avatarVideo?.fileUrl || "",
          fileName: location.avatarVideo?.fileName || "",
          enabled: location.avatarVideo?.enabled || false,
          videoDuration: location.avatarVideo?.videoDuration ?? 0,
          captionFile: location.avatarVideo?.captionFile ?? ''
        },

        // Enable custom avatar video
        enableCustomAvatarVideo: location.enableCustomAvatarVideo || false,

        // Map Video
        mapVideo: {
          fileUrl: location.mapVideo?.fileUrl || "",
          fileName: location.mapVideo?.fileName || "",
          enabled: location.mapVideo?.enabled || false,
          videoDuration: location.mapVideo?.videoDuration ?? 0,
        },

        // Enable map video
        enableMapVideo: location.enableMapVideo || false,

        // Map delay time
        mapDelayTime: location.mapDelayTime || 2,

        // QR Code
        qrCode: location.qrCode || {},

        // Operation hours
        operationHours: location.operationHours || [
          {
            day: "monday",
            open: "09:00",
            close: "17:00",
            isOpen: true,
            isFullDay: false,
          },
          {
            day: "tuesday",
            open: "09:00",
            close: "17:00",
            isOpen: true,
            isFullDay: false,
          },
          {
            day: "wednesday",
            open: "09:00",
            close: "17:00",
            isOpen: true,
            isFullDay: false,
          },
          {
            day: "thursday",
            open: "09:00",
            close: "17:00",
            isOpen: true,
            isFullDay: false,
          },
          {
            day: "friday",
            open: "09:00",
            close: "17:00",
            isOpen: true,
            isFullDay: false,
          },
          {
            day: "saturday",
            open: "10:00",
            close: "16:00",
            isOpen: true,
            isFullDay: false,
          },
          {
            day: "sunday",
            open: "10:00",
            close: "16:00",
            isOpen: true,
            isFullDay: false,
          },
        ],

        // Button settings
        globalSettings: {
          ...item.globalSettings,
          ...location.globalSettings,
          shadow: {
            // ...item.globalSettings.shadow,
            ...location.globalSettings?.shadow,
          },
          highlight: {
            // ...item.globalSettings.highlight,
            ...location.globalSettings?.highlight,
          },
        },

        customSettings: {
          ...item.customSettings,
          ...location.customSettings,
          shadow: {
            ...item.customSettings.shadow,
            ...location.customSettings?.shadow,
          },
          highlight: {
            ...item.customSettings.highlight,
            ...location.customSettings?.highlight,
          },
        },

        // Uploaded URLs
        uploadedUrls: {
          upperLeft: {
            ...item.uploadedUrls.upperLeft,
            ...location.uploadedUrls?.upperLeft,
          },
          upperRight: {
            ...item.uploadedUrls.upperRight,
            ...location.uploadedUrls?.upperRight,
          },
          bottomLeft: {
            ...item.uploadedUrls.bottomLeft,
            ...location.uploadedUrls?.bottomLeft,
          },
          bottomRight: {
            ...item.uploadedUrls.bottomRight,
            ...location.uploadedUrls?.bottomRight,
          },
          backButton: {
            ...item.uploadedUrls.backButton,
            ...location.uploadedUrls?.backButton,
          },
          locationPhoto: {
            ...item.uploadedUrls.locationPhoto,
            ...location.uploadedUrls?.locationPhoto,
          },
        },

        // Parent references
        parentCategories: location.parentCategories || [],
        parentSubcategories: location.parentSubcategories || [],
        subLocations: location.subLocations || [],
        runOnlyAvatarVideo: location.runOnlyAvatarVideo || false,

        totalSteps: location.totalSteps || 0,

        logoBeforeMap: {
          fileUrl: location.logoBeforeMap?.fileUrl || "",
          fileName: location.logoBeforeMap?.fileName || "",
          enabled: location.logoBeforeMap?.enabled || false,
          width: location.logoBeforeMap?.width || 100,
          displayDuration: location.logoBeforeMap?.displayDuration || 3,
        },

        mapPosition: {
          widthPx: location.mapPosition?.widthPx,
          heightPx: location.mapPosition?.heightPx,
          top: location.mapPosition?.top || null,
          right: location.mapPosition?.right || null,
          left: location.mapPosition?.left || null,
          bottom: location.mapPosition?.bottom || null,
        },

        stepDetails: location.stepDetails || [],

        stepsFetched: location.stepsFetched || false,

        staticMapDuration: location.staticMapDuration || 2,

        enableQrCode: location.enableQrCode || true,

        textDirectionsMultiLang: location.textDirectionsMultiLang || {},

        avatarVideosMultiLang: location.avatarVideosMultiLang || {},

        tags: location.tags || [],

        distanceFromStart: location.distanceFromStart ?? null,
      };

      console.log("Fetched location data:", locationData);

      setItem(locationData);

      // Store original text directions for comparison
      setOriginalTextDirections(locationData.textDirections || "");
    } catch (err) {
      console.error("Failed to load location:", err);
      showErrorToast("Failed to load location data");
    } finally {
      setLoading(false);
    }
  };

  // Handle text directions update
  const handleTextDirectionsUpdate = (newTextDirections, updateResult) => {
    // Update local state
    setItem((prev) => ({
      ...prev,
      textDirections: newTextDirections,
    }));

    // Update original text directions
    setOriginalTextDirections(newTextDirections);

    // Show success message
    showSuccessToast(
      `Text directions updated. ${updateResult.totalVideosRegenerated} videos are being regenerated.`,
    );

    // Refresh location data to get updated information
    setTimeout(() => {
      fetchLocationData();
    }, 2000);
  };

  // Resolve button settings based on enabled state
  const resolvedButtonSettings = useMemo(() => {
    return item.enabled ? item.customSettings : item.globalSettings;
  }, [item.globalSettings, item.customSettings, item.enabled]);

  // Dropzone configurations
  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps } =
    useDropzone({
      accept: { "image/*": [] },
      maxFiles: 1,
      onDrop: ([file]) => {
        setPhotoFile(file);
      },
    });

  const {
    getRootProps: getAvatarVideoRootProps,
    getInputProps: getAvatarVideoInputProps,
  } = useDropzone({
    accept: { "video/*": [] },
    maxFiles: 1,
    onDrop: ([file]) => {
      setAvatarVideoFile(file);
    },
  });

  const {
    getRootProps: getMapVideoRootProps,
    getInputProps: getMapVideoInputProps,
  } = useDropzone({
    accept: { "video/*": [] },
    maxFiles: 1,
    onDrop: ([file]) => {
      setMapVideoFile(file);
    },
  });

  const {
    getRootProps: getLogoBeforeMapRootProps,
    getInputProps: getLogoBeforeMapInputProps,
  } = useDropzone({
    accept: { "image/*": [] },
    maxFiles: 1,
    onDrop: ([file]) => {
      setLogoBeforeMapFile(file);
    },
  });

  const {
    getRootProps: getQrCodeRootProps,
    getInputProps: getQrCodeInputProps,
  } = useDropzone({
    accept: { "image/*": [] },
    maxFiles: 1,
    onDrop: ([file]) => {
      setQrCodeFile(file);
    },
  });

  const { getRootProps: getPngRootProps, getInputProps: getPngInputProps } =
    useDropzone({
      accept: { "image/png": [] },
      maxFiles: 1,
      onDrop: ([file]) => {
        setPngFile(file);
      },
    });

  const {
    getRootProps: getClickSoundRootProps,
    getInputProps: getClickSoundInputProps,
  } = useDropzone({
    accept: { "audio/*": [] },
    maxFiles: 1,
    onDrop: ([file]) => {
      setClickSoundFile(file);
    },
  });

  const useCornerLogoDropzone = (setter) => {
    return useDropzone({
      accept: { "image/*": [] },
      maxFiles: 1,
      onDrop: ([file]) => {
        setter(file);
      },
    });
  };

  const upperLeftDropzone = useCornerLogoDropzone(setUpperLeftLogoFile);
  const upperRightDropzone = useCornerLogoDropzone(setUpperRightLogoFile);
  const bottomLeftDropzone = useCornerLogoDropzone(setBottomLeftLogoFile);
  const bottomRightDropzone = useCornerLogoDropzone(setBottomRightLogoFile);
  const backButtonDropzone = useCornerLogoDropzone(setBackButtonFile);

  // Upload handlers for MainWayfinderMedia
  const FILE_STATE_MAP = {
    upperLeft: setUpperLeftLogoFile,
    upperRight: setUpperRightLogoFile,
    bottomLeft: setBottomLeftLogoFile,
    bottomRight: setBottomRightLogoFile,
    backButton: setBackButtonFile,
    locationPhoto: setPhotoFile,
  };

  const handleUploadMedia = useCallback(
    async (file, position) => {
      if (!file) {
        showErrorToast(`No file selected for ${position}`);
        return;
      }

      try {
        setUploadingStates((prev) => ({ ...prev, [position]: true }));

        const fileUrl = await uploadToS3(file);

        setItem((prev) => ({
          ...prev,
          uploadedUrls: {
            ...prev.uploadedUrls,
            [position]: {
              ...prev.uploadedUrls[position],
              fileUrl,
              fileName: file.name,
              enabled: true,
            },
          },
        }));

        showSuccessToast(`${position} uploaded successfully`);

        // Auto clear the correct file state
        FILE_STATE_MAP[position]?.(null);
      } catch (error) {
        console.error(error);
        showErrorToast(`Failed to upload ${position}`);
      } finally {
        setUploadingStates((prev) => ({ ...prev, [position]: false }));
      }
    },
    [uploadToS3, showErrorToast, showSuccessToast],
  );

  // Delete handler for MainWayfinderMedia
  const handleDeleteMedia = useCallback(
    async (position) => {
      if (!window.confirm(`Delete ${position}?`)) return;

      try {
        setLoadingMedia(true);

        // Update local state immediately
        setItem((prev) => ({
          ...prev,
          uploadedUrls: {
            ...prev.uploadedUrls,
            [position]: {
              fileUrl: "",
              fileName: "",
              enabled: false,
              width: 100,
              height: 100,
            },
          },
        }));

        // You might want to call an API here to delete from backend
        // await axios.delete(`/api/media/${userId}/${position}`);

        showSuccessToast(`${position} deleted successfully`);
      } catch (error) {
        showErrorToast(`Failed to delete ${position}`);
        console.error(error);
      } finally {
        setLoadingMedia(false);
      }
    },
    [userId, showErrorToast, showSuccessToast],
  );

  // Upload handlers for specific files
  const handleUploadPhoto = async () => {
    await handleUploadMedia(photoFile, "locationPhoto");
  };

  const handleUploadBackButton = async () => {
    await handleUploadMedia(backButtonFile, "backButton");
  };

  const handleUploadAvatarVideo = async () => {
    if (!avatarVideoFile) {
      showErrorToast("No avatar video file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(avatarVideoFile);

      setItem((prev) => ({
        ...prev,
        avatarVideo: {
          ...prev.avatarVideo,
          fileUrl: fileUrl,
          fileName: avatarVideoFile.name,
          enabled: true,
        },
      }));

      setAvatarVideoFile(null);
    } catch (error) {
      showErrorToast("Failed to upload avatar video");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadMapVideo = async () => {
    if (!mapVideoFile) {
      showErrorToast("No map video file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(mapVideoFile);

      setItem((prev) => ({
        ...prev,
        mapVideo: {
          ...prev.mapVideo,
          fileUrl: fileUrl,
          fileName: mapVideoFile.name,
          enabled: true,
        },
      }));

      setMapVideoFile(null);
    } catch (error) {
      showErrorToast("Failed to upload map video");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadLogoBeforeMap = async () => {
    if (!logoBeforeMapFile) {
      showErrorToast("No logo file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(logoBeforeMapFile);

      setItem((prev) => ({
        ...prev,
        logoBeforeMap: {
          ...prev.logoBeforeMap,
          fileUrl: fileUrl,
          fileName: logoBeforeMapFile.name,
          enabled: true,
        },
      }));

      setLogoBeforeMapFile(null);

    } catch (error) {
      showErrorToast("Failed to upload logo before map");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadQrCode = async () => {
    if (!qrCodeFile) {
      showErrorToast("No QR code file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(qrCodeFile);

      setItem((prev) => ({
        ...prev,
        qrCode: {
          ...prev.qrCode,
          fileUrl: fileUrl,
          fileName: qrCodeFile.name,
          enabled: true,
        },
      }));

      setQrCodeFile(null);
      showSuccessToast("QR code uploaded successfully");
    } catch (error) {
      showErrorToast("Failed to upload QR code");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPng = async () => {
    if (!pngFile) {
      showErrorToast("No PNG file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(pngFile);

      const settingsPath = item.custom ? "customSettings" : "globalSettings";

      setItem((prev) => {
        const existingBtnPng = prev[settingsPath]?.btnPng;
        const existingBtnPngFileName = prev[settingsPath]?.btnPngFileName;

        // Build language-keyed objects, preserving other languages
        const updatedBtnPng = typeof existingBtnPng === 'object' && existingBtnPng !== null
          ? { ...existingBtnPng, [currentLanguage]: fileUrl }
          : { en: existingBtnPng || fileUrl, [currentLanguage]: fileUrl };

        const updatedBtnPngFileName = typeof existingBtnPngFileName === 'object' && existingBtnPngFileName !== null
          ? { ...existingBtnPngFileName, [currentLanguage]: pngFile.name }
          : { en: existingBtnPngFileName || pngFile.name, [currentLanguage]: pngFile.name };

        return {
          ...prev,
          [settingsPath]: {
            ...prev[settingsPath],
            btnPng: updatedBtnPng,
            btnPngFileName: updatedBtnPngFileName,
            pngBtnenabled: true,
          },
        };
      });

      setPngFile(null);
      showSuccessToast("Button PNG uploaded successfully");
    } catch (error) {
      showErrorToast("Failed to upload PNG");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClickSound = async () => {
    if (!clickSoundFile) {
      showErrorToast("No click sound file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(clickSoundFile);

      const settingsPath = item.enabled ? "customSettings" : "globalSettings";

      setItem((prev) => ({
        ...prev,
        [settingsPath]: {
          ...prev[settingsPath],
          clickSound: fileUrl,
          clickSoundFilename: clickSoundFile.name,
        },
      }));

      setClickSoundFile(null);
      showSuccessToast("Click sound uploaded successfully");
    } catch (error) {
      showErrorToast("Failed to upload click sound");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadIcon = async () => {
    if (!btnIcon) {
      showErrorToast("No icon file selected");
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(btnIcon);

      const settingsPath = item.custom ? "customSettings" : "globalSettings";

      setItem((prev) => ({
        ...prev,
        [settingsPath]: {
          ...prev[settingsPath],
          buttonIcon: fileUrl,
          buttonIconFileName: btnIcon.name,
          enableIcon: true,
        },
      }));

      setBtnIcon(null);
      // showSuccessToast("Button icon uploaded successfully");
    } catch (error) {
      showErrorToast("Failed to upload icon");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Corner logo upload handlers
  const handleUploadCornerLogo = async (corner, file, setter) => {
    if (!file) {
      showErrorToast(`No ${corner} logo file selected`);
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(file);

      setItem((prev) => ({
        ...prev,
        uploadedUrls: {
          ...prev.uploadedUrls,
          [corner]: {
            ...prev.uploadedUrls[corner],
            fileUrl: fileUrl,
            fileName: file.name,
            enabled: true,
          },
        },
      }));

      setter(null);
      showSuccessToast(`${corner} logo uploaded successfully`);
    } catch (error) {
      showErrorToast(`Failed to upload ${corner} logo`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Save button text specifically
  const handleSaveButtonText = async () => {
    console.log("Saving button text...");

    try {
      setLoading(true);

      const res = await axios.patch(
        `/api/wayfinder/locations/${locationId}/button-text`,
        {
          buttonText: item.buttonText,
          userId,
          language: currentLanguage,
        },
      );

      if (res.data.success) {
        showSuccessToast("Button text updated successfully");
        fetchLocationData();
      } else {
        showErrorToast(res.data.message || "Failed to update button text");
      }
    } catch (error) {
      console.error("Button text update error:", error);
      showErrorToast(
        error.response?.data?.message || "Failed to update button text",
      );
    } finally {
      setLoading(false);
    }
  };

  // Save location
  const handleSave = async () => {
    console.log("Saving location...");
    // e.preventDefault();

    try {
      setLoading(true);

      const saveData = {
        ...item,
        userId,
        globalSettings: item.globalSettings || {},
        customSettings: item.customSettings || {},
      };

      // Check if text directions have changed
      const textDirectionsChanged =
        originalTextDirections !== item.textDirections &&
        item.textDirections &&
        item.textDirections.trim() !== "";

      if (textDirectionsChanged) {
        // Show confirmation dialog for text direction change
        const confirmRegenerate = window.confirm(
          `Text directions have changed from:\n"${originalTextDirections || "No text"}"\n\nto:\n"${item.textDirections}"\n\nThis will regenerate ALL existing videos for this location in 4K resolution. Continue?`,
        );

        if (confirmRegenerate) {
          // Use the enhanced video service to update text directions and regenerate videos
          const regenerateResponse = await axios.put(
            `/api/wayfinder/locations/${locationId}/update-text-and-regenerate`,
            {
              textDirections: item.textDirections.trim(),
              resolution: "4k",
            },
          );

          if (regenerateResponse.data.success) {
            const result = regenerateResponse.data.data;
            showSuccessToast(
              `Text directions updated and ${result.totalVideosRegenerated} videos are being regenerated in 4K resolution.`,
            );

            // Update original text directions
            setOriginalTextDirections(item.textDirections.trim());

            // Refresh location data
            setTimeout(() => {
              fetchLocationData();
            }, 2000);
          } else {
            showErrorToast(
              regenerateResponse.data.message || "Failed to regenerate videos",
            );
          }
        } else {
          // User cancelled, revert text directions
          setItem((prev) => ({
            ...prev,
            textDirections: originalTextDirections,
          }));
          showErrorToast("Text direction update cancelled");
          setLoading(false);
          return;
        }
      } else {
        // Normal save without text direction change
        const res = await axios.put(
          `/api/wayfinder/locations/${locationId}`,
          saveData,
        );

        if (res.data.success) {
          showSuccessToast("Location saved successfully");
          fetchLocationData();
        } else {
          showErrorToast(res.data.message || "Save failed");
        }
      }
    } catch (error) {
      console.error("Save error:", error);
      showErrorToast(error.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  // Delete location
  const handleDelete = async () => {
    if (!window.confirm("Delete this location permanently?")) return;

    try {
      await axios.delete(
        `/api/wayfinder/location/${locationId}?userId=${userId}`,
      );
      showSuccessToast("Location deleted successfully");
      navigate(`/wayfinder-main/${userId}`);
    } catch (error) {
      showErrorToast("Delete failed");
      console.error(error);
    }
  };

  // Reset to original data
  const handleReset = () => {
    fetchLocationData();
    setPhotoFile(null);
    setAvatarVideoFile(null);
    setMapVideoFile(null);
    setLogoBeforeMapFile(null);
    setQrCodeFile(null);
    setPngFile(null);
    setClickSoundFile(null);
    setBtnIcon(null);
    setUpperLeftLogoFile(null);
    setUpperRightLogoFile(null);
    setBottomLeftLogoFile(null);
    setBottomRightLogoFile(null);
    setBackButtonFile(null);
  };

  // Handle operation hours change

  // Handle corner logo property change
  const handleCornerLogoChange = (corner, field, value) => {
    setItem((prev) => ({
      ...prev,
      uploadedUrls: {
        ...prev.uploadedUrls,
        [corner]: {
          ...prev.uploadedUrls[corner],
          [field]: value,
        },
      },
    }));
  };

  // Handle corner logo toggle
  const handleCornerLogoToggle = (corner, enabled) => {
    setItem((prev) => ({
      ...prev,
      uploadedUrls: {
        ...prev.uploadedUrls,
        [corner]: {
          ...prev.uploadedUrls[corner],
          enabled,
        },
      },
    }));
  };

  // Handle general item change
  const handleItemChange = (field, value) => {
    const keys = field.split(".");

    setItem((prev) => {
      const updatedItem = { ...prev };
      let current = updatedItem;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        } else {
          current[keys[i]] = { ...current[keys[i]] };
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updatedItem;
    });
  };

  // Handle button settings change
  const handleSettingsChange = (key, value) => {
    const settingsPath = item.enabled ? "customSettings" : "globalSettings";
    handleItemChange(`${settingsPath}.${key}`, value);
  };

  // Toggle location enabled state
  const toggleLocationEnabled = () => {
    setItem((prev) => ({ ...prev, enabled: !prev.enabled }));
  };

  return (
    <WayfinderLocationTemplate
      type="location"
      userId={userId}
      locationId={locationId}
      item={item}
      setItem={setItem}
      resolvedButtonSettings={resolvedButtonSettings}
      loading={loading || s3Uploading}
      buttonColor={buttonColor}
      // Language props
      currentLanguage={currentLanguage}
      onLanguageChange={updateLanguage}
      // Text direction update props
      onTextDirectionsUpdate={handleTextDirectionsUpdate}
      // File props for button settings tab
      photoFile={photoFile}
      avatarVideoFile={avatarVideoFile}
      mapVideoFile={mapVideoFile}
      logoBeforeMapFile={logoBeforeMapFile}
      qrCodeFile={qrCodeFile}
      pngFile={pngFile}
      clickSoundFile={clickSoundFile}
      btnIcon={btnIcon}
      backButtonFile={backButtonFile}
      // Corner logo files
      upperLeftLogoFile={upperLeftLogoFile}
      upperRightLogoFile={upperRightLogoFile}
      bottomLeftLogoFile={bottomLeftLogoFile}
      bottomRightLogoFile={bottomRightLogoFile}
      // Dropzone props for button settings tab
      getPhotoRootProps={getPhotoRootProps}
      getPhotoInputProps={getPhotoInputProps}
      getAvatarVideoRootProps={getAvatarVideoRootProps}
      getAvatarVideoInputProps={getAvatarVideoInputProps}
      getQrCodeRootProps={getQrCodeRootProps}
      getQrCodeInputProps={getQrCodeInputProps}
      getPngRootProps={getPngRootProps}
      getPngInputProps={getPngInputProps}
      getClickSoundRootProps={getClickSoundRootProps}
      getClickSoundInputProps={getClickSoundInputProps}
      // Corner logo dropzone props
      getUpperLeftLogoRootProps={upperLeftDropzone.getRootProps}
      getUpperLeftLogoInputProps={upperLeftDropzone.getInputProps}
      getUpperRightLogoRootProps={upperRightDropzone.getRootProps}
      getUpperRightLogoInputProps={upperRightDropzone.getInputProps}
      getBottomLeftLogoRootProps={bottomLeftDropzone.getRootProps}
      getBottomLeftLogoInputProps={bottomLeftDropzone.getInputProps}
      getBottomRightLogoRootProps={bottomRightDropzone.getRootProps}
      getBottomRightLogoInputProps={bottomRightDropzone.getInputProps}
      getBackButtonRootProps={backButtonDropzone.getRootProps}
      getBackButtonInputProps={backButtonDropzone.getInputProps}
      // Upload handlers for button settings tab
      handleUploadPhoto={handleUploadPhoto}
      handleUploadBackButton={handleUploadBackButton}
      handleUploadAvatarVideo={handleUploadAvatarVideo}
      handleUploadMapVideo={handleUploadMapVideo}
      handleUploadLogoBeforeMap={handleUploadLogoBeforeMap}
      handleUploadQrCode={handleUploadQrCode}
      handleUploadPng={handleUploadPng}
      handleUploadClickSound={handleUploadClickSound}
      handleUploadIcon={handleUploadIcon}
      // Corner logo upload handlers for button settings tab
      handleUploadUpperLeftLogo={() =>
        handleUploadCornerLogo(
          "upperLeft",
          upperLeftLogoFile,
          setUpperLeftLogoFile,
        )
      }
      handleUploadUpperRightLogo={() =>
        handleUploadCornerLogo(
          "upperRight",
          upperRightLogoFile,
          setUpperRightLogoFile,
        )
      }
      handleUploadBottomLeftLogo={() =>
        handleUploadCornerLogo(
          "bottomLeft",
          bottomLeftLogoFile,
          setBottomLeftLogoFile,
        )
      }
      handleUploadBottomRightLogo={() =>
        handleUploadCornerLogo(
          "bottomRight",
          bottomRightLogoFile,
          setBottomRightLogoFile,
        )
      }
      // MainWayfinderMedia props
      handleUploadMedia={handleUploadMedia}
      handleDeleteMedia={handleDeleteMedia}
      loadingMedia={loadingMedia}
      uploadingStates={uploadingStates}
      setUploadingStates={setUploadingStates}
      // Other handlers
      handleSave={handleSave}
      handleSaveButtonText={handleSaveButtonText}
      handleDelete={handleDelete}
      handleReset={handleReset}
      handleItemChange={handleItemChange}
      handleSettingsChange={handleSettingsChange}
      handleCornerLogoChange={handleCornerLogoChange}
      handleCornerLogoToggle={handleCornerLogoToggle}
      toggleLocationEnabled={toggleLocationEnabled}
      // File setters
      setPhotoFile={setPhotoFile}
      setAvatarVideoFile={setAvatarVideoFile}
      setMapVideoFile={setMapVideoFile}
      setLogoBeforeMapFile={setLogoBeforeMapFile}
      setQrCodeFile={setQrCodeFile}
      setPngFile={setPngFile}
      setClickSoundFile={setClickSoundFile}
      setBtnIcon={setBtnIcon}
      setUpperLeftLogoFile={setUpperLeftLogoFile}
      setUpperRightLogoFile={setUpperRightLogoFile}
      setBottomLeftLogoFile={setBottomLeftLogoFile}
      setBottomRightLogoFile={setBottomRightLogoFile}
      setBackButtonFile={setBackButtonFile}
      fetchLocationData={fetchLocationData}

      setCurrentLanguage={updateLanguage}
      
      // Step-by-Step Directions props
      stepByStepConfig={stepByStepConfig}
      setStepByStepConfig={setStepByStepConfig}
      saveStepByStepConfig={saveStepByStepConfig}
      parentOptions={parentOptions}
    />
  );
};

export default WayfinderLocation;
